import { useId, useState } from 'react';
import type { FormEvent } from 'react';

import { requestMagicLink, startGoogleLogin } from '../api';
import { Logo } from '../components/Logo';
import { useSignInError } from '../components/Onboarding';
import { t } from '../i18n';
import { useTheme } from '../theme';

type View = 'form' | 'pending';
type Status = 'idle' | 'submitting' | 'error';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Login screen (productspec §5): magic link by email, or Google OIDC. The
 * server answers 202 for any address so the form cannot be used to find out
 * who has an account.
 */
export function LoginPage(): React.ReactNode {
  const { preference, setPreference } = useTheme();
  const signInError = useSignInError();
  const [view, setView] = useState<View>('form');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>('idle');
  const [sentTo, setSentTo] = useState('');

  const emailId = useId();
  const errorId = useId();

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const value = email.trim().toLowerCase();
    if (!value) {
      setError(t('auth.email.required'));
      return;
    }
    if (!EMAIL_RE.test(value)) {
      setError(t('auth.email.invalid'));
      return;
    }

    setError(null);
    setStatus('submitting');
    try {
      await requestMagicLink(value);
      setSentTo(value);
      setView('pending');
    } catch {
      setError(t('error.generic'));
    } finally {
      setStatus('idle');
    }
  }

  return (
    <main className="login">
      <div className="login__toolbar">
        <label className="login__theme" htmlFor="theme-select">
          <span className="sr-only">{t('theme.label')}</span>
          <select
            id="theme-select"
            value={preference}
            onChange={(e) => setPreference(e.currentTarget.value as 'light' | 'dark' | 'system')}
          >
            <option value="system">{t('theme.system')}</option>
            <option value="light">{t('theme.light')}</option>
            <option value="dark">{t('theme.dark')}</option>
          </select>
        </label>
      </div>

      <section className="login__card" aria-labelledby="login-title">
        <Logo className="login__logo" width={148} />
        <h1 id="login-title" className="login__title">
          {view === 'form' ? t('auth.title') : t('auth.pending.title')}
        </h1>

        {view === 'form' ? (
          <>
            {signInError ? (
              <p className="login__error" role="alert">
                {t(signInError)}
              </p>
            ) : null}

            <p className="login__subtitle">{t('auth.subtitle')}</p>

            <form className="login__form" onSubmit={(e) => void onSubmit(e)} noValidate>
              <div className="login__field">
                <label className="login__label" htmlFor={emailId}>
                  {t('auth.email.label')}
                </label>
                <input
                  id={emailId}
                  className="login__input"
                  type="email"
                  name="email"
                  autoComplete="email"
                  inputMode="email"
                  placeholder={t('auth.email.placeholder')}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.currentTarget.value);
                    if (error) setError(null);
                  }}
                  aria-invalid={error ? true : undefined}
                  aria-describedby={error ? errorId : undefined}
                  autoFocus
                />
                {error ? (
                  <p className="login__error" id={errorId} role="alert">
                    {error}
                  </p>
                ) : null}
              </div>

              <button
                className="login__button login__button--primary"
                type="submit"
                disabled={status === 'submitting'}
              >
                {status === 'submitting' ? '…' : t('auth.submit.email')}
              </button>
            </form>

            <div className="login__divider" role="separator">
              <span className="login__divider-text">{t('auth.divider')}</span>
            </div>

            <button
              className="login__button login__button--secondary"
              type="button"
              onClick={startGoogleLogin}
            >
              <GoogleIcon />
              {t('auth.submit.google')}
            </button>
          </>
        ) : (
          <>
            <p className="login__subtitle">{t('auth.pending.body', { email: sentTo })}</p>
            <div className="login__actions">
              <button
                className="login__button login__button--secondary"
                type="button"
                onClick={() => setView('form')}
              >
                {t('auth.pending.back')}
              </button>
              <button
                className="login__button login__button--primary"
                type="button"
                disabled={status === 'submitting'}
                onClick={() => {
                  setEmail(sentTo);
                  setView('form');
                }}
              >
                {t('auth.pending.resend')}
              </button>
            </div>
          </>
        )}
      </section>
    </main>
  );
}

function GoogleIcon(): React.ReactNode {
  return (
    <svg className="login__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"
      />
    </svg>
  );
}
